import { createRouter, createWebHistory } from "vue-router";
import LoginView from "../views/LoginView.vue";

import AdminLayout from "@/layouts/AdminLayout.vue";
import MemberLayout from "@/layouts/MemberLayout.vue"; // if you have it, else remove

import DashboardView from "@/views/admin/DashboardView.vue"; // your admin overview page
import MembersView from "@/views/admin/MembersView.vue";
import AdminBulkContributions from "@/views/admin/AdminBulkContributions.vue";

const routes = [
  // Guest
  {
    path: "/login/sms",
    name: "login-sms",
    component: () => import("@/views/LoginViewSMS.vue"),
    meta: { guest: true },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: { guest: true },
  },

  // Member area (nest under MemberLayout if you have it)
  {
    path: "/me",
    component: MemberLayout, // remove this wrapper if you don't have MemberLayout
    meta: { auth: true },
    children: [
      {
        path: "",
        name: "member-dashboard",
        component: () => import("@/views/me/MemberDashboardView.vue"),
      },
      {
        path: "statement",
        name: "my-statements",
        component: () => import("@/views/me/MyStatementView.vue"),
      },
      {
        path: "contributions",
        name: "my-contributions",
        component: () => import("@/views/me/MyContributionsView.vue"),
      },
      {
        path: "loans",
        name: "my-loans",
        component: () => import("@/views/me/MyLoans.vue"),
      },
      {
        path: "/me/my-loan-applications",
        name: "my-loan-applications",
        component: () => import("@/views/me/MyLoanApplicationsView.vue"),
      },
      {
        path: "/me/withdrawals",
        name: "my-withdrawals",
        component: () => import("@/views/me/MyWithdrawalsView.vue"),
      },
      {
        path: "penalties",
        name: "my-penalties",
        component: () => import("@/views/me/MyPenaltiesView.vue"),
      },
    ],
  },

  // Admin area (nest under AdminLayout)
  {
    path: "/admin",
    component: AdminLayout,
    meta: { auth: true, roles: ["admin", "treasurer"] },
    children: [
      {
        path: "",
        name: "admin-dashboard",
        component: DashboardView,
      },
      {
        path: "members",
        name: "admin-members",
        component: MembersView,
      },
      {
        path: "expenses",
        name: "admin-expenses",
        component: () => import("@/views/expenses/ExpensesView.vue"),
      },
      {
        path: "contributions",
        name: "admin-contributions",
        component: () => import("@/views/admin/AdminContributionView.vue"),
      },
      {
        path: "commitments",
        name: "admin-commitments",
        component: () => import("@/views/admin/AdminCommitmentView.vue"),
      },
      {
        path: "contributions/bulk",
        name: "admin-contributions-bulk",
        component: AdminBulkContributions,
      },
      {
        path: "/admin/loans/import",
        name: "AdminLoanBulkImport",
        component: () => import("@/views/admin/AdminLoanBulkImport.vue"),
        meta: { requiresAuth: true, roles: ["admin", "treasurer"] },
      },
      {
        path: "/admin/loans/repayment-sheet",
        name: "LoanRepaymentSheet",
        component: () => import("@/views/admin/LoanRepaymentSheetView.vue"),
        meta: { requiresAuth: true, roles: ["admin", "treasurer"] },
      },
      {
        path: "/admin/reconciliation",
        name: "Reconciliation",
        component: () => import("@/views/admin/ReconciliationView.vue"),
        meta: { requiresAuth: true, roles: ["admin", "treasurer"] },
      },
      {
        path: "/admin/loans",
        name: "admin-loans",
        component: () => import("@/views/admin/AdminLoansView.vue"),
      },
      {
        path: "/admin/loan-applications-review",
        name: "loan-applications-review",
        component: () => import("@/views/admin/LoanApplicationsReviewView.vue"),
      },
      {
        path: "/admin/reports/monthly-matrix",
        name: "MonthlyMatrixReport",
        component: () => import("@/views/admin/reports/MonthlyMatrixReportView.vue"),
      },
      {
        path: "/admin/withdrawals",
        name: "withdrawal-review",
        component: () => import("@/views/admin/WithdrawalReviewView.vue"),
      },
      {
        path: "penalties",
        name: "admin-penalties",
        component: () => import("@/views/admin/AdminPenalties.vue"),
      },
      {
        path: "settings/financial-years",
        name: "admin-settings-financial-years",
        component: () => import("@/views/admin/SettingsFinancialYearRules.vue"),
      },
      {
        path: "statement",
        name: "admin-statement",
        component: () => import("@/views/admin/AdminStatementView.vue"),
      },
      {
        path: "profit-cycle",
        name: "admin-profit-cycle",
        component: () => import("@/views/admin/ProfitCyclesAdmin.vue"),
      },
      {
        path: "/admin/members/:id/financial-statement",
        name: "MemberFinancialStatement",
        component: () =>
          import("@/views/admin/MemberFinancialStatementView.vue"),
        meta: { requiresAuth: true, roles: ["admin", "treasurer"] },
      },
      {
        path: "/admin/payroll-batches",
        name: "PayrollBatches",
        component: () => import("@/views/admin/PayrollBatchesView.vue"),
        meta: { requiresAuth: true, roles: ["admin", "treasurer"] },
      },
    ],
  },

  // Redirect root based on role
  {
    path: "/",
    redirect: () => {
      const user = getUser();
      if (!user) return "/login/sms";
      return ["admin", "treasurer"].includes(user.role) ? "/admin" : "/me";
    },
  },

  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

function isAuthed() {
  return !!localStorage.getItem("token");
}

function getUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

router.beforeEach((to) => {
  // Needs auth but not logged in
  if (to.meta.auth && !isAuthed()) return { path: "/login/sms" };

  // Guest route but already logged in
  if (to.meta.guest && isAuthed()) {
    const user = getUser();
    if (!user) return { path: "/login/sms" };
    return ["admin", "treasurer"].includes(user.role)
      ? { path: "/admin" }
      : { path: "/me" };
  }

  // Role-based access
  const user = getUser();
  if (to.meta.roles && user && !to.meta.roles.includes(user.role)) {
    return { path: "/me" };
  }
});

export default router;
